/**
 * 우 검토 모드 패널 — 분류사례 + 호해설 발췌 + 관련법령 (Perplexity 풍).
 *
 * Props:
 *  - hs: 4-digit (heading hierarchy 단계에서 사용)
 *  - hsk: 10-digit (세번 단계에서 사용 — 더 정확)
 *  - chapter: 2-digit (호 list 단계 — 더 넓음)
 *  - heading_popum_ko: 호 popum 한글 본문 (호 단계에서 inline 표시용)
 *  - lang: 'kor' | 'eng'
 *
 * 우선순위: hsk > hs > chapter. 가장 구체적인 context 사용.
 */
import React, { useState, useEffect } from "react";
import SourceEvidenceLink, { precedentEvidence } from "./SourceEvidenceLink";

// 매칭 token 노란 highlight (LeftAiPanel 의 동일 함수와 같음 — 추후 utils 로 분리).
function highlightTokens(text, tokens) {
  if (!text || !tokens || tokens.length === 0) return text;
  const lowText = String(text).toLowerCase();
  const ranges = [];
  for (const tk of tokens) {
    if (!tk || tk.length < 2) continue;
    const t = String(tk).toLowerCase();
    let p = 0;
    while ((p = lowText.indexOf(t, p)) !== -1) {
      ranges.push([p, p + t.length]);
      p += t.length;
    }
  }
  if (ranges.length === 0) return text;
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const r of ranges) {
    if (merged.length && merged[merged.length - 1][1] >= r[0]) {
      merged[merged.length - 1][1] = Math.max(
        merged[merged.length - 1][1],
        r[1],
      );
    } else {
      merged.push([r[0], r[1]]);
    }
  }
  const parts = [];
  let last = 0;
  merged.forEach(([s, e], i) => {
    if (last < s) parts.push(text.slice(last, s));
    parts.push(
      <mark
        key={`m_${i}`}
        style={{ background: "#fff3a0", color: "inherit", padding: "0 1px" }}
      >
        {text.slice(s, e)}
      </mark>,
    );
    last = e;
  });
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

// 결정문 수집 과정에서 유실된 목록 줄바꿈을 화면에서 복원한다.
// 원문 데이터는 바꾸지 않고 `ㅇ`(상위 판단)과 `-`(하위 근거)의 계층만 표시용으로 정돈한다.
export function rulingLines(text) {
  if (text == null) return [];
  return String(text)
    .replace(/\r/g, "")
    .replace(/\s*ㅇ\s+(?=[가-힣])/g, "\nㅇ ")
    .replace(/[ \t]+-\s+(?=[가-힣제“\"'])/g, "\n- ")
    .replace(/\s*※\s*/g, "\n※ ")
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function RulingText({ text, tokens }) {
  const lines = rulingLines(text);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      {lines.map((line, i) => {
        const top = line.startsWith("ㅇ");
        const child = line.startsWith("-");
        const note = line.startsWith("※");
        const marker = top ? "ㅇ" : child ? "–" : note ? "※" : null;
        const content = marker ? line.slice(1).trim() : line;
        return (
          <div
            key={`${i}-${line.slice(0, 12)}`}
            style={{
              display: marker ? "grid" : "block",
              gridTemplateColumns: marker ? "15px minmax(0, 1fr)" : undefined,
              gap: marker ? 3 : undefined,
              paddingLeft: child ? 17 : 0,
              marginTop: top && i > 0 ? 7 : 0,
              color: note ? "var(--audit-text-muted)" : "var(--audit-text)",
              lineHeight: 1.7,
            }}
          >
            {marker && <span aria-hidden="true" style={{ color: top ? "var(--audit-accent)" : "var(--audit-text-muted)", fontWeight: top ? 700 : 500 }}>{marker}</span>}
            <span>{highlightTokens(content, tokens)}</span>
          </div>
        );
      })}
    </div>
  );
}

// 매칭 모드 배지 — 분류사례 row 가 어떤 방식으로 hit 됐는지 (✓ 키워드 / 의미 / 둘 다).
// 무채색 (테두리 + 텍스트) 만으로 절제. 모드 별 라벨 텍스트로 구분.
function MatchModeBadge({ mode }) {
  if (!mode) return null;
  const text = {
    keyword: "✓ 키워드",
    semantic: "의미",
    both: "✓ 키워드 · 의미",
  }[mode];
  if (!text) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        fontSize: 12,
        fontWeight: 600,
        background: "var(--audit-bg)",
        color: "var(--audit-text-muted)",
        border: "1px solid var(--audit-border)",
        borderRadius: 10,
        lineHeight: 1.4,
      }}
    >
      {text}
    </span>
  );
}

export default function EvidencePanel({
  hs,
  hsk,
  chapter,
  heading_popum_ko,
  lang,
  onExpandChange,
  searchContext,
  focusedCase, // AI 분류 evidence 카드 사례 클릭 시 표시
  onClearFocusedCase,
}) {
  // focusedCase.hsk 가 있으면 우측 panel 전체 (precedent/partner/laws) 그 hsk 의 evidence 로 override.
  // URL (가운데 panel) 은 유지 — 사용자가 같은 화면에서 다른 사례의 근거자료 확인.
  const eHsk = focusedCase?.hsk ? String(focusedCase.hsk) : hsk;
  const eHs = focusedCase?.hsk ? String(focusedCase.hsk).slice(0, 4) : hs;
  const eChapter = focusedCase?.hsk
    ? String(focusedCase.hsk).slice(0, 2)
    : chapter;
  const overridden = !!focusedCase?.hsk && eHsk !== hsk;
  // 포커스(후보 클릭) 상태 — 키워드 전역검색 대신 그 세번으로 "필터" 동작.
  //   (키워드가 있으면 backend 가 hsk 필터를 무시(전역)하므로, q 를 빼서 코드 필터 활성.)
  const eSearchContext = focusedCase?.hsk ? null : searchContext;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "var(--audit-text-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        근거 자료 (검토 모드)
        {overridden && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 12,
              padding: "1px 5px",
              background: "#dbeafe",
              color: "#1a73e8",
              border: "1px solid #1a73e8",
              borderRadius: 3,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            ▷ {eHsk} 사례 보는 중
          </span>
        )}
      </div>
      {focusedCase && (
        <FocusedCaseExcerpt item={focusedCase} onClose={onClearFocusedCase} />
      )}
      {heading_popum_ko && !overridden && (
        <PopumExcerpt text={heading_popum_ko} lang={lang} />
      )}
      <PrecedentsExcerpt
        hs={eHs}
        hsk={eHsk}
        chapter={eChapter}
        onExpandChange={onExpandChange}
        searchContext={eSearchContext}
      />
      <PartnerExcerpt
        hs={eHs}
        hsk={eHsk}
        chapter={eChapter}
        searchContext={eSearchContext}
      />
      <LawsExcerpt hs={eHs} hsk={eHsk} />
    </div>
  );
}

function FocusedCaseExcerpt({ item, onClose }) {
  if (!item) return null;
  const type = item.type || "precedent";
  const titleByType = {
    precedent: "AI 분류 사례 (선택됨)",
    master: "관세율표 본문 (선택됨)",
    embedding: "AI 의미 분석 HSK (선택됨)",
  };
  return (
    <div
      style={{
        ...cardStyle,
        background: "#dbeafe",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6,
        }}
      >
        <h3 style={cardTitleStyle}>
          {titleByType[type] || titleByType.precedent}
        </h3>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: "2px 8px",
              border: "1px solid var(--audit-border)",
              background: "var(--audit-bg)",
              color: "var(--audit-text-muted)",
              borderRadius: 3,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            ✕ 닫기
          </button>
        )}
      </div>
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 4,
        }}
      >
        <code
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--audit-text)",
          }}
        >
          {item.hsk}
        </code>
        {item.source && (
          <span
            style={{
              fontSize: 12,
              padding: "1px 5px",
              background: "var(--audit-bg)",
              border: "1px solid var(--audit-border)",
              borderRadius: 3,
              color: "var(--audit-text-muted)",
            }}
          >
            {item.source}
          </span>
        )}
        {item.case_id && (
          <code style={{ fontSize: 12, color: "var(--audit-text-muted)" }}>
            {item.case_id}
          </code>
        )}
        {type === "embedding" && item.n != null && (
          <span style={{ fontSize: 12, color: "var(--audit-text-muted)" }}>
            의미 유사 {item.n}건
          </span>
        )}
        {type === "master" && item.matched_n != null && (
          <span
            style={{
              fontSize: 12,
              color: item.pass ? "var(--audit-text-muted)" : "#d93025",
            }}
          >
            역검증 {item.matched_n}/{item.total_tokens} {item.pass ? "✓" : "✗"}
          </span>
        )}
      </div>
      {/* precedent: label + snippet */}
      {type === "precedent" && item.label && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--audit-text)",
            marginBottom: 4,
          }}
        >
          {item.label}
        </div>
      )}
      {type === "precedent" && item.snippet && (
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.6,
            color: "var(--audit-text)",
            whiteSpace: "pre-wrap",
          }}
        >
          {item.snippet}
        </div>
      )}
      {/* master: name_ko + popum */}
      {type === "master" && (item.name_ko || item.popum) && (
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.6,
            color: "var(--audit-text)",
          }}
        >
          {item.name_ko && <div>{item.name_ko}</div>}
          {item.popum && (
            <div style={{ color: "var(--audit-text-muted)" }}>{item.popum}</div>
          )}
        </div>
      )}
      {/* embedding: 안내 */}
      {type === "embedding" && (
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.6,
            color: "var(--audit-text-muted)",
          }}
        >
          AI 의미 vector top-20 중 {item.n}건이 이 HSK 와 같음
        </div>
      )}
    </div>
  );
}

function PopumExcerpt({ text, lang }) {
  // 하단 '호해설' 탭과 이중 노출 인지(문의: "6201 호 검색 시 우측·하단 호해설
  // 이중") — 기본 접힘. 접힘 상태는 첫 줄 + 하단 탭 안내만, 펼치면 전문.
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const firstLine = (text.split(/\r?\n/).find((l) => l.trim()) || "").trim();
  const preview =
    firstLine.length > 120 ? firstLine.slice(0, 120) + "…" : firstLine;
  return (
    <div style={cardStyle}>
      <h3 style={cardTitleStyle}>호해설 발췌</h3>
      <div style={{ fontSize: 12, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
        {expanded ? text : preview}
      </div>
      {expanded ? (
        <button onClick={() => setExpanded(false)} style={moreBtnStyle}>
          [접기]
        </button>
      ) : (
        <div style={{ display: "flex", gap: 10, alignItems: "baseline" }}>
          {text.length > preview.length && (
            <button onClick={() => setExpanded(true)} style={moreBtnStyle}>
              [펼치기]
            </button>
          )}
          <span style={{ fontSize: 12, color: "var(--audit-text-faint)" }}>
            전문·소호해설은 하단 ‘호해설’ 탭
          </span>
        </div>
      )}
    </div>
  );
}

// 티어 필터 칩 — 전체 / 정확(🟢) / 부분(🟡).
function TierChip({ label, active, color, dot, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 12,
        padding: "2px 9px",
        borderRadius: 11,
        cursor: "pointer",
        fontFamily: "inherit",
        border: `1px solid ${active ? color : "#dadce0"}`,
        background: active ? color : "#fff",
        color: active ? "#fff" : color,
        fontWeight: active ? 600 : 400,
      }}
    >
      {dot && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: active ? "#fff" : dot,
            flexShrink: 0,
          }}
        />
      )}
      {label}
    </button>
  );
}

function PrecedentsExcerpt({
  hs,
  hsk,
  chapter,
  onExpandChange,
  searchContext,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [tierFilter, setTierFilter] = useState("all"); // all | exact | and
  // 결과내 좁히기 (P1) — 결정코드 facet 필터 + 결과내 재검색 텍스트.
  const [codeFilter, setCodeFilter] = useState(null); // 선택된 호 4자리 | null
  const [refineText, setRefineText] = useState(""); // 입력값
  const [refineApplied, setRefineApplied] = useState(""); // 적용된 값(Enter)

  const q = (searchContext && searchContext.q) || "";
  const semanticOn = !!(searchContext && searchContext.semanticOn);
  const expand = !!(searchContext && searchContext.expand);
  const ctxTokens =
    (searchContext && Array.isArray(searchContext.matchTokens)
      ? searchContext.matchTokens
      : null) || null;

  // expand 상태 변화 시 parent 에 알림 (panel width 가변 trigger)
  useEffect(() => {
    if (onExpandChange) onExpandChange(expandedIdx !== null);
  }, [expandedIdx, onExpandChange]);

  // 검색 조건 변경 시 expand + 티어/결과내필터 초기화
  useEffect(() => {
    setExpandedIdx(null);
    setTierFilter("all");
    setCodeFilter(null);
    setRefineText("");
    setRefineApplied("");
  }, [hs, hsk, chapter, q, semanticOn]);

  useEffect(() => {
    const u = new URL("/api/onto/precedents-search", window.location.origin);
    u.searchParams.set("q", q);
    // priority: hsk (10-digit 정확) > hs (4-digit prefix) > chapter. q 있어도 hs filter 유지.
    if (hsk) {
      const norm = String(hsk).replace(/\D/g, "");
      if (norm.length === 10) {
        u.searchParams.set("hsk", norm);
      } else if (norm.length === 6) {
        u.searchParams.set("hs6", norm);
      } else {
        u.searchParams.set("hs4", norm.slice(0, 4));
      }
    } else if (hs) {
      u.searchParams.set("hs4", String(hs).replace(/\D/g, "").slice(0, 4));
    } else if (chapter && !q) {
      u.searchParams.set("hs4", String(chapter).padStart(2, "0") + "00");
    }
    // 지연로드: 특정 호(hs)·세번(hsk)·검색어(q) 없이 류(chapter)만 브라우징 중이면 근거 fetch 안 함.
    if (!q && !hsk && !hs) {
      setData({ items: [] });
      setLoading(false);
      return;
    }
    // 질의가 있으면 항상 hybrid — keyword 전역검색(hs 미적용·57만행 스캔=~22s) 회피.
    // hybrid는 hs필터 적용 + RRF(키워드+의미)로 ~1.5s이며 결과도 이 세번에 정확.
    u.searchParams.set("mode", q ? "hybrid" : "keyword");
    u.searchParams.set("limit", "20");
    if (expand) u.searchParams.set("expand", "true");
    if (codeFilter) u.searchParams.set("code", codeFilter);
    if (refineApplied) u.searchParams.set("refine", refineApplied);
    setLoading(true);
    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [hs, hsk, chapter, q, semanticOn, expand, codeFilter, refineApplied]);

  // backend 응답 match_tokens 가 분해/translate 결과 — context 보다 정확
  const tks =
    data && Array.isArray(data.match_tokens) && data.match_tokens.length > 0
      ? data.match_tokens
      : ctxTokens || [];

  if (loading) {
    return (
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>관세청 분류사례</h3>
        <div style={mutedStyle}>불러오는 중…</div>
      </div>
    );
  }
  const allItems = data?.items || [];
  if (allItems.length === 0) {
    return (
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>관세청 분류사례</h3>
        <div style={mutedStyle}>
          {q ? `"${q}" 매칭 사례 없음` : "관련 사례 없음"}
        </div>
      </div>
    );
  }
  // 의도 랭킹 3티어 — 정확(합성어) / 원형(토큰 AND) / 확장(동의어). tier_total(전체 기준).
  const tt = data?.tier_total;
  const tiered = !!tt;
  const cnt = (k) =>
    tt ? tt[k] || 0 : allItems.filter((i) => i.tier === k).length;
  const exactN = cnt("exact");
  const literalN = cnt("literal");
  const expandedN = cnt("expanded");
  const items =
    tiered && tierFilter !== "all"
      ? allItems.filter((it) => (it.tier || "expanded") === tierFilter)
      : allItems;
  // 국내 vs 해외 분리. source='clip_foreign' 가 해외, 그 외는 국내.
  const domestic = items.filter((it) => it.source !== "clip_foreign");
  const foreign = items.filter((it) => it.source === "clip_foreign");
  // 카드 헤더 진짜 총계 — 전체보기일 때만 source_total(limit 무관). 필터 시엔 표시중 건수.
  const st = data?.source_total;
  const domTotal = tierFilter === "all" && st ? st.domestic : domestic.length;
  const forTotal = tierFilter === "all" && st ? st.foreign : foreign.length;
  const facetCodes = data?.facets?.codes || [];
  const refineOn = !!(refineApplied || codeFilter);
  return (
    <>
      {q && (facetCodes.length > 0 || refineOn) && (
        <div
          style={{
            marginBottom: 8,
            padding: "8px 10px",
            border: "1px solid var(--audit-border)",
            borderRadius: 6,
            background: "var(--audit-bg-subtle)",
          }}
        >
          {/* 결과내 재검색 (텍스트 AND) */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setRefineApplied(refineText.trim());
              }}
              placeholder="결과내 재검색 (Enter)"
              style={{
                flex: 1,
                fontSize: 12,
                padding: "4px 8px",
                border: "1px solid var(--audit-border)",
                borderRadius: 4,
                fontFamily: "inherit",
                background: "var(--audit-bg)",
                color: "var(--audit-text)",
              }}
            />
            {refineOn && (
              <button
                onClick={() => {
                  setRefineText("");
                  setRefineApplied("");
                  setCodeFilter(null);
                }}
                style={{
                  fontSize: 12,
                  padding: "4px 8px",
                  border: "1px solid var(--audit-border)",
                  borderRadius: 4,
                  background: "var(--audit-bg)",
                  cursor: "pointer",
                  color: "var(--audit-text-muted)",
                  whiteSpace: "nowrap",
                }}
              >
                필터 해제
              </button>
            )}
          </div>
          {/* 활성 필터 표시 */}
          {refineOn && (
            <div
              style={{
                fontSize: 12,
                color: "#5f6368",
                marginTop: 6,
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <span>필터:</span>
              {codeFilter && (
                <span
                  onClick={() => setCodeFilter(null)}
                  style={{
                    cursor: "pointer",
                    color: "#1a73e8",
                    fontWeight: 600,
                  }}
                >
                  코드 {codeFilter} ✕
                </span>
              )}
              {refineApplied && (
                <span
                  onClick={() => {
                    setRefineText("");
                    setRefineApplied("");
                  }}
                  style={{
                    cursor: "pointer",
                    color: "#1a73e8",
                    fontWeight: 600,
                  }}
                >
                  "{refineApplied}" ✕
                </span>
              )}
            </div>
          )}
          {/* 결정코드 facet 칩 — 키워드가 퍼진 코드를 한 번에 좁히기 */}
          {facetCodes.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 5,
                flexWrap: "wrap",
                alignItems: "center",
                marginTop: 8,
              }}
            >
              <span style={{ fontSize: 12, color: "#5f6368", marginRight: 2 }}>
                결정코드
              </span>
              <TierChip
                label="전체"
                active={!codeFilter}
                color="#5f6368"
                onClick={() => setCodeFilter(null)}
              />
              {facetCodes.map((c) => (
                <TierChip
                  key={c.code}
                  label={`${c.code}${c.name ? " " + (c.name.length > 10 ? c.name.slice(0, 10) + "…" : c.name) : ""} ${c.n}`}
                  active={codeFilter === c.code}
                  color="#1a73e8"
                  onClick={() =>
                    setCodeFilter(codeFilter === c.code ? null : c.code)
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}
      {tiered && (
        <div
          style={{
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            marginBottom: 8,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "#5f6368" }}>
            "{data.exact_compound}"
          </span>
          <TierChip
            label={`전체 ${exactN + literalN + expandedN}`}
            active={tierFilter === "all"}
            color="#5f6368"
            onClick={() => setTierFilter("all")}
          />
          {exactN > 0 && (
            <TierChip
              label={`정확 ${exactN}`}
              active={tierFilter === "exact"}
              color="#188038"
              dot="#188038"
              onClick={() => setTierFilter("exact")}
            />
          )}
          <TierChip
            label={`원형 ${literalN}`}
            active={tierFilter === "literal"}
            color="#AD6800"
            dot="#E8A100"
            onClick={() => setTierFilter("literal")}
          />
          <TierChip
            label={`확장 ${expandedN}`}
            active={tierFilter === "expanded"}
            color="#5f6368"
            dot="#9aa0a6"
            onClick={() => setTierFilter("expanded")}
          />
        </div>
      )}
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>관세청 분류사례 (국내, {domTotal})</h3>
        {domestic.length === 0 ? (
          <div style={mutedStyle}>국내 사례 없음</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {domestic.slice(0, 20).map((it, i) => {
              const isExpanded = expandedIdx === `d${i}`;
              return (
                <PrecedentRow
                  key={`d${i}`}
                  item={it}
                  expanded={isExpanded}
                  onToggle={() => setExpandedIdx(isExpanded ? null : `d${i}`)}
                  matchTokens={tks}
                />
              );
            })}
            {domTotal > Math.min(domestic.length, 20) && (
              <div style={mutedStyle}>
                … 외 {domTotal - Math.min(domestic.length, 20)}건
              </div>
            )}
          </div>
        )}
      </div>
      {foreign.length > 0 && (
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>관세청 해외 분류사례 ({forTotal})</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {foreign.slice(0, 20).map((it, i) => {
              const isExpanded = expandedIdx === `f${i}`;
              return (
                <PrecedentRow
                  key={`f${i}`}
                  item={it}
                  expanded={isExpanded}
                  onToggle={() => setExpandedIdx(isExpanded ? null : `f${i}`)}
                  matchTokens={tks}
                />
              );
            })}
            {forTotal > Math.min(foreign.length, 20) && (
              <div style={mutedStyle}>
                … 외 {forTotal - Math.min(foreign.length, 20)}건
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// 개별 분류사례 row — 클릭 시 본문 inline expand (Perplexity 풍).
// HeadingTabs 가운데 패널 분류사례 탭에서도 재사용 (export).
// matchTokens 전달 시 본문 노란 highlight + matchMode 배지 (✓ 키워드 / 의미 / 둘 다).
export function PrecedentRow({ item, expanded, onToggle, matchTokens }) {
  const sourceLabels = {
    clip_kcs: "CLIP",
    kcs_pre_ruling: "분류원",
    bigdata_customs: "bigdata",
    clip_foreign: "해외",
  };
  const COUNTRY_LABEL = {
    US: "🇺🇸 미국",
    EU: "🇪🇺 EU",
    GB: "🇬🇧 영국",
    CN: "🇨🇳 중국",
    JP: "🇯🇵 일본",
    TW: "🇹🇼 대만",
    CL: "🇨🇱 칠레",
    AU: "🇦🇺 호주",
    TH: "🇹🇭 태국",
    SG: "🇸🇬 싱가포르",
    PH: "🇵🇭 필리핀",
    KH: "🇰🇭 캄보디아",
    IN: "🇮🇳 인도",
    CA: "🇨🇦 캐나다",
    BD: "🇧🇩 방글라데시",
    CH: "🇨🇭 스위스",
  };
  const srcTag = sourceLabels[item?.source] || item?.source || "";
  const countryTag =
    item?.source === "clip_foreign" && item?.country
      ? COUNTRY_LABEL[item.country] || item.country
      : null;
  const desc = (item?.desc || "").trim();
  const reasoning = (item?.reasoning || "").trim();
  const label = (item?.label || "").trim();
  // case_id 에서 source prefix 제거 → 참조번호
  const refNo = (item?.case_id || "").replace(/^(CLIP-|KCS-|BD-)/, "");
  const tks = Array.isArray(matchTokens) ? matchTokens : [];
  const matchMode = item?.match_mode || null;
  const isForeign = item?.source === "clip_foreign";
  // 좌측 색 보더 — 🟢 정확(합성어) / 🟡 원형(토큰 AND) / ⚪ 확장(동의어).
  const tierColor =
    item?.tier === "exact"
      ? "#188038"
      : item?.tier === "literal"
        ? "#E8A100"
        : item?.tier === "expanded"
          ? "#bdc1c6"
          : null;
  return (
    <div
      style={{
        ...precedentItemStyle,
        background: expanded ? "var(--audit-bg)" : "transparent",
        borderLeft: `${tierColor ? 3 : 2}px solid ${expanded ? "var(--audit-accent)" : tierColor || "var(--audit-border)"}`,
      }}
    >
      <div
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        style={{
          cursor: "pointer",
          padding: "2px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "baseline",
            marginBottom: 3,
          }}
        >
          <span
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 12,
              color: "var(--audit-accent)",
              fontWeight: 600,
            }}
          >
            {item.hsk || item.hs6 || ""}
          </span>
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              fontStyle: "italic",
            }}
          >
            {srcTag}
          </span>
          {countryTag && (
            <span
              style={{
                fontSize: 12,
                color: "var(--audit-text-muted)",
                fontWeight: 600,
              }}
            >
              {countryTag}
            </span>
          )}
          <MatchModeBadge mode={matchMode} />
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-faint)",
              marginLeft: "auto",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            {(item.decision_date || "").slice(0, 10)}
          </span>
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              userSelect: "none",
            }}
          >
            {expanded ? "▾" : "▸"}
          </span>
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--audit-text)",
            lineHeight: 1.55,
            display: !expanded && isForeign ? "-webkit-box" : "block",
            WebkitLineClamp: !expanded && isForeign ? 2 : undefined,
            WebkitBoxOrient: !expanded && isForeign ? "vertical" : undefined,
            overflow: !expanded && isForeign ? "hidden" : "visible",
          }}
        >
          {highlightTokens(label.slice(0, expanded ? 1000 : 120), tks)}
        </div>
        {!expanded && isForeign && label.length > 120 && (
          <div style={{ marginTop: 4, fontSize: 11, color: "var(--audit-text-muted)" }}>
            상세 근거 펼치기
          </div>
        )}
      </div>
      {expanded && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px dashed var(--audit-border)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {isForeign && (
            <div
              style={{
                padding: "8px 9px",
                borderRadius: 5,
                background: "var(--audit-bg-subtle)",
                border: "1px solid var(--audit-border)",
                color: "var(--audit-text-muted)",
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              해외 참고사례이며 대한민국 품목분류에 직접적인 법적 구속력은 없습니다.
            </div>
          )}
          <KV label="참조번호" value={refNo} mono />
          {desc && desc !== label && (
            <KV label="물품설명" value={<RulingText text={desc} tokens={tks} />} block />
          )}
          {reasoning && (
            <KV
              label="결정사유"
              value={<RulingText text={reasoning} tokens={tks} />}
              block
              emphasized
            />
          )}
        </div>
      )}
    </div>
  );
}

// 관세사 사례 section (trade_partner_history, 4M, anonymous A~E).
// 항상 mount (우측 패널 영구 section). hs filter + q 모두 활용.
function PartnerExcerpt({ hs, hsk, chapter, searchContext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [qEng, setQEng] = useState(""); // 한글 q → 영문 변환 결과
  const q = (searchContext && searchContext.q) || "";
  const expand = !!(searchContext && searchContext.expand);
  const ctxTokens =
    (searchContext && Array.isArray(searchContext.matchTokens)
      ? searchContext.matchTokens
      : null) || [];

  // 한글 keyword 자동 영문 변환 — trade_partner_history.line_item 가 영문 입력이라 매칭 필수.
  // 영문 keyword 면 그대로 사용.
  useEffect(() => {
    if (!q) {
      setQEng("");
      return;
    }
    if (!/[가-힣]/.test(q)) {
      setQEng(q);
      return;
    }
    const ctl = new AbortController();
    fetch(`/api/onto/translate?q=${encodeURIComponent(q)}&target=en`, {
      signal: ctl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((td) => {
        if (td && td.translated) setQEng(td.translated);
        else setQEng(q); // 변환 fail 시 원본 한글
      })
      .catch(() => setQEng(q));
    return () => ctl.abort();
  }, [q]);

  useEffect(() => {
    setExpandedIdx(null);
  }, [hs, hsk, chapter, qEng]);

  useEffect(() => {
    // 한글 q + 영문 변환 대기 중 → fetch skip (qEng 도착 시 자동 재실행)
    if (q && /[가-힣]/.test(q) && !qEng) return;
    const effQ = qEng || q;

    // hs4 도출 (hsk > hs). 지연로드: chapter(류)만이면 hs4Val="" → 아래 agency fetch skip(호/세번 선택 시에만).
    let hs4Val = "";
    if (hsk) {
      const norm = String(hsk).replace(/\D/g, "");
      hs4Val = norm.length >= 4 ? norm.slice(0, 4) : "";
    } else if (hs) {
      hs4Val = String(hs).replace(/\D/g, "").slice(0, 4);
    }

    // 품명(q) 없으면 → 이 호 전체 관세사 사례 (agency-precedents, 하단 탭과 동일 소스),
    // 품명 있으면 → 품명 텍스트 매칭 (partner-search).
    if (!effQ) {
      if (!hs4Val) {
        setData({ items: [] });
        setLoading(false);
        return;
      }
      setLoading(true);
      fetch(`/api/onto/agency-precedents?hs4=${hs4Val}&limit=20`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          // agency-precedents item → PartnerRow 형태로 매핑
          const mapped = (d?.items || []).map((it) => ({
            label: it.line_item,
            spec: it.spec,
            hsk: it.hsk,
            decl_date: it.last_date,
          }));
          setData({ items: mapped });
        })
        .finally(() => setLoading(false));
      return;
    }

    const u = new URL("/api/onto/partner-search", window.location.origin);
    u.searchParams.set("q", effQ);
    if (hsk) {
      const norm = String(hsk).replace(/\D/g, "");
      if (norm.length === 10) u.searchParams.set("hsk", norm);
      else if (norm.length >= 4) u.searchParams.set("hs4", norm.slice(0, 4));
    } else if (hs) {
      u.searchParams.set("hs4", String(hs).replace(/\D/g, "").slice(0, 4));
    } else if (hs4Val) {
      u.searchParams.set("hs4", hs4Val);
    }
    u.searchParams.set("limit", "20");
    if (expand) u.searchParams.set("expand", "true");
    setLoading(true);
    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [hs, hsk, chapter, qEng, q, expand]);

  const tks =
    data && Array.isArray(data.match_tokens) && data.match_tokens.length > 0
      ? data.match_tokens
      : ctxTokens;

  if (loading) {
    return (
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>🏷 관세사 분류사례</h3>
        <div style={mutedStyle}>불러오는 중…</div>
      </div>
    );
  }
  const items = data?.items || [];
  if (items.length === 0) {
    return (
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>🏷 관세사 분류사례</h3>
        <div style={mutedStyle}>
          {q ? `"${q}" 관세사 매칭 없음` : "관세사 사례 없음"}
        </div>
      </div>
    );
  }
  return (
    <div style={cardStyle}>
      <h3 style={cardTitleStyle}>🏷 관세사 분류사례 ({items.length})</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.slice(0, 20).map((it, i) => {
          const isExpanded = expandedIdx === i;
          return (
            <PartnerRow
              key={i}
              item={it}
              expanded={isExpanded}
              onToggle={() => setExpandedIdx(isExpanded ? null : i)}
              matchTokens={tks}
            />
          );
        })}
        {items.length > 20 && (
          <div style={mutedStyle}>… 외 {items.length - 20}건</div>
        )}
      </div>
    </div>
  );
}

// 관세사 사례 row — partner-search 결과. 출처 익명, 회색톤 절제.
function PartnerRow({ item, expanded, onToggle, matchTokens }) {
  const tks = Array.isArray(matchTokens) ? matchTokens : [];
  const label = (item?.label || "").trim();
  const spec = (item?.spec || "").trim();
  return (
    <div
      style={{
        ...precedentItemStyle,
        background: expanded ? "var(--audit-bg)" : "transparent",
        borderLeft: `2px solid ${expanded ? "var(--audit-accent)" : "var(--audit-border)"}`,
      }}
    >
      <div onClick={onToggle} style={{ cursor: "pointer", padding: "2px 0" }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "baseline",
            marginBottom: 3,
          }}
        >
          <span
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: 12,
              color: "var(--audit-accent)",
              fontWeight: 600,
            }}
          >
            {item.hsk || item.hs4 || ""}
          </span>
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-faint)",
              marginLeft: "auto",
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            {(item.decl_date || "").slice(0, 10)}
          </span>
          <span
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              userSelect: "none",
            }}
          >
            {expanded ? "▾" : "▸"}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "var(--audit-text)" }}>
          {highlightTokens(label.slice(0, expanded ? 1000 : 120), tks)}
        </div>
      </div>
      {expanded && spec && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: "var(--audit-text-muted)",
            paddingLeft: 8,
            borderLeft: "1px dashed var(--audit-border)",
          }}
        >
          <b>규격:</b> {highlightTokens(spec, tks)}
        </div>
      )}
    </div>
  );
}

function KV({ label, value, mono, block, emphasized }) {
  if (!value) return null;
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "var(--audit-text-muted)",
          marginBottom: 3,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        ▸ {label}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "var(--audit-text)",
          lineHeight: 1.6,
          fontFamily: mono ? "JetBrains Mono, monospace" : "inherit",
          whiteSpace: block ? "pre-wrap" : "normal",
          wordBreak: "break-word",
          padding: emphasized ? "10px 11px" : 0,
          background: emphasized ? "var(--audit-bg-subtle)" : "transparent",
          border: emphasized ? "1px solid var(--audit-border)" : "none",
          borderRadius: emphasized ? 6 : 0,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// source 별 link/tag 분기 — 무료 사이트 (관세청 unipass) 만 deep-link
//  - clip_kcs        → 관세청 CLIP 분류사례 (deep-link, ↗)
//  - kcs_pre_ruling  → 관세평가분류원 결정사례 (unipass URL, ↗)
//  - bigdata_customs → 빅데이터 포털 (POST popup, deep-link 불가, label only)
//  - 회원제 clhs URL 은 DB 단계에서 모두 NULL 처리 완료 (2026-05-26)
function SourceLink({ item }) {
  const src = item?.source || "";
  const url = item?.decision_url;
  const labels = {
    clip_kcs: { tag: "CLIP", title: "관세청 CLIP 분류사례" },
    kcs_pre_ruling: { tag: "분류원", title: "관세평가분류원 결정사례" },
    bigdata_customs: {
      tag: "bigdata",
      title: "관세청 빅데이터 포털 (deep-link 불가)",
    },
  };
  const meta = labels[src] || { tag: src || "?", title: "" };
  const evidence = precedentEvidence({
    decisionUrl: url,
    caseId: item?.case_id,
    source: src,
    code: item?.hsk || item?.hs6,
    refNo: item?.ref_no,
  });
  if (evidence.url) {
    return <SourceEvidenceLink compact {...evidence} />;
  }
  // url 없음 — 출처 라벨만
  return (
    <span
      title={meta.title}
      style={{
        fontSize: 12,
        color: "var(--audit-text-muted)",
        fontFamily: "JetBrains Mono, monospace",
        fontStyle: "italic",
      }}
    >
      {meta.tag}
    </span>
  );
}

function LawsExcerpt({ hs, hsk }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hs && !hsk) {
      setData({ items: [] });
      setLoading(false);
      return;
    }
    const u = new URL("/api/onto/laws-search", window.location.origin);
    u.searchParams.set("q", "");
    u.searchParams.set("hsk", hsk || hs);
    u.searchParams.set("mode", "keyword");
    u.searchParams.set("limit", "5");
    setLoading(true);
    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [hs, hsk]);

  if (loading) {
    return (
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>관련법령</h3>
        <div style={mutedStyle}>불러오는 중…</div>
      </div>
    );
  }
  const items = data?.items || [];
  if (items.length === 0) {
    return (
      <div style={cardStyle}>
        <h3 style={cardTitleStyle}>관련법령</h3>
        <div style={mutedStyle}>관련 법령 없음</div>
      </div>
    );
  }
  return (
    <div style={cardStyle}>
      <h3 style={cardTitleStyle}>관련법령 ({items.length})</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.slice(0, 5).map((it, i) => (
          <div key={i} style={lawItemStyle}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--audit-text)",
                marginBottom: 3,
              }}
            >
              {(it.label || "").slice(0, 80)}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--audit-text-muted)",
                lineHeight: 1.55,
              }}
            >
              {(it.desc || "").slice(0, 200)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const cardStyle = {
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg-elevated)",
  borderRadius: 6,
  padding: "10px 12px",
};

const cardTitleStyle = {
  margin: "0 0 8px",
  fontSize: 12,
  fontWeight: 700,
  color: "var(--audit-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const mutedStyle = {
  fontSize: 12,
  color: "var(--audit-text-muted)",
  fontStyle: "italic",
};

const moreBtnStyle = {
  display: "block",
  background: "transparent",
  border: "none",
  padding: "4px 0 0",
  cursor: "pointer",
  color: "var(--audit-accent)",
  fontSize: 12,
  fontFamily: "inherit",
};

const precedentItemStyle = {
  paddingLeft: 10,
  borderLeft: "2px solid var(--audit-border)",
};

const lawItemStyle = {
  paddingLeft: 10,
  borderLeft: "2px solid var(--audit-border)",
};
